import React, {MouseEvent, useEffect, useState} from 'react';
import {I18n} from 'react-redux-i18n';

import {InputAdornment, IconButton, WithStyles, withStyles} from '@material-ui/core';
import {VisibilityOff, Visibility} from '@material-ui/icons';

import {useDispatch, useSelector} from 'react-redux';
import {Dropdown, RenderDivOrEmpty, RenderOrEmpty, Switch} from '../../components';
import {CustomButton, CustomInput} from '../../material-dashboard-pro-react/components';

import {DataExportModel, DataExportType} from '../../types/DataExport';
import {DataExportConnectionTestResult} from './types';
import {DATA_EXPORT_REDUCER} from '../DataExportEditForm/slice';
import {isURLWithProtocol} from '../../utils/validators';
import {sendTestConnectionToHEC} from './action';

import style from './style';

/* eslint-disable no-unused-vars */
type DataExportSettingsTabProps = WithStyles<typeof style> & {
  onFieldChange: (key: keyof DataExportModel, value: any) => void;
  onChangeDataExportTemplate: (data: DataExportModel) => void;
  onMultipleFieldsChange: (values: Partial<DataExportModel>) => void;
  setAreSpecificFieldsValid: (isValid: boolean) => void;
  dataExport: DataExportModel;
  isEditMode: boolean;
};
/* eslint-enable no-unused-vars */

function DataExportSettingsTab({
  classes,
  onFieldChange,
  onChangeDataExportTemplate,
  onMultipleFieldsChange,
  setAreSpecificFieldsValid,
  dataExport,
  isEditMode,
}: DataExportSettingsTabProps) {
  const dispatch = useDispatch();

  const dataExports: DataExportModel[]
    = useSelector((store: any) => store[DATA_EXPORT_REDUCER].dataExports);
  const [exportTemplateOptions, setExportTemplateOptions]
    = useState<{ label: string, value: any }[]>([]);
  const [selectedExportTemplateIndex, setSelectedExportTemplateIndex] = useState(0);
  const [selectedStreamTypeIndex, setSelectedStreamTypeIndex] = useState<number | null>(null);
  const [showToken, setShowToken] = useState(false);
  const [collectorUrlError, setCollectorUrlError] = useState<string | null>(null);
  const [connectionTest, setConnectionTest] = useState<DataExportConnectionTestResult | null>(null);

  const formControlProps = {
    fullWidth: true,
  };

  const dataExportTypes: DataExportType[] = useSelector((store: any) =>
    store[DATA_EXPORT_REDUCER].dataExportTypes || []);

  const validateSpecificFields = (
    specificData: { [key: string]: string | number | boolean | undefined },
  ) => {
    const selectedStreamType = dataExportTypes[selectedStreamTypeIndex ?? 0];
    const areRequiredFieldsValid = selectedStreamType.specificSettings
      .filter((s) => s.required)
      .every(
        (s) => {
          const val = specificData[s.name];
          return !!val || val === 0 || val === false;
        },
      );
    setAreSpecificFieldsValid(areRequiredFieldsValid);
  };

  const changeStreamType = (index: number) => {
    const selectedType = dataExportTypes[index];

    const updatedFields: Partial<DataExportModel> = {
      streamTypeId: selectedType.id,
    };

    if (
      selectedType.useDefaultCollectorUrlOnly ||
      (selectedType.defaultCollectorUrl && !dataExport.collectorUrl &&
        dataExport.collectorUrl !== selectedType.defaultCollectorUrl)
    ) {
      updatedFields.collectorUrl = selectedType.defaultCollectorUrl ?? '';
    }

    if (selectedType.useDefaultFieldMappingOnly) {
      updatedFields.builtinFieldMappingId = selectedType.defaultFieldMappingId;
    }
    if (!selectedType.isCustomFieldMappingAllowed) {
      updatedFields.customFieldMapping = undefined;
      updatedFields.builtinFieldMappingId = selectedType.defaultFieldMappingId;
    }

    onMultipleFieldsChange(updatedFields);
  };

  const handleStreamTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const index = Number(e.target.value);
    setSelectedStreamTypeIndex(index);
  };

  const handleCollectorUrlChange = (value: string) => {
    onFieldChange('collectorUrl', value);
    if (value && !isURLWithProtocol(value)) {
      setCollectorUrlError(I18n.t('dataExportPage.editForm.errors.validationErrorForURL'));
    } else {
      setCollectorUrlError(null);
    }
  };

  const handleChangeExportTemplate = (e: React.ChangeEvent<HTMLInputElement>) => {
    const templateId = exportTemplateOptions[Number(e.target.value)].value;
    if (templateId && templateId.length > 0) {
      const template = dataExports.find((exportTemplate) => exportTemplate.id === templateId);
      if (template) {
        onChangeDataExportTemplate(template);
      }
    }
    setSelectedExportTemplateIndex(
      exportTemplateOptions.map((option) => option.value).indexOf(templateId));
  };

  useEffect(() => {
    if (dataExportTypes.length > 0) {
      const typeIndex = dataExportTypes.findIndex(
        (type: { id: string; }) => type.id === dataExport.streamTypeId);
      if (typeIndex >= 0) {
        setSelectedStreamTypeIndex(typeIndex);
      } else {
        setSelectedStreamTypeIndex(0);
        onFieldChange('streamTypeId', dataExportTypes[0].id);
      }
    }
  }, [dataExport?.streamTypeId, dataExportTypes]);

  useEffect(() => {
    if (dataExportTypes && dataExportTypes.length > 0 && selectedStreamTypeIndex != null) {
      changeStreamType(selectedStreamTypeIndex);
      validateSpecificFields(dataExport.siemSpecificData);
    }
  }, [selectedStreamTypeIndex, dataExportTypes]);

  useEffect(() => {
    const expOptions = [{
      label: I18n.t('dataExportPage.editForm.settings.labels.noTemplateSelected'),
      value: '',
    }].concat(dataExports.map((exportTemplate) => ({
      label: exportTemplate.name,
      value: exportTemplate.id || '',
    })));

    setExportTemplateOptions(expOptions);
  }, [dataExports]);

  useEffect(() => {
    setConnectionTest(null);
    if (!isEditMode) {
      setShowToken(false);
      setSelectedExportTemplateIndex(0);
    }
  }, [isEditMode]);

  const getConnectionTestResultMessage = () => {
    if (!connectionTest) {
      return '';
    }
    if (connectionTest.success) {
      return I18n.t('dataExportPage.editForm.settings.buttonTest.success');
    }
    return I18n.t(
      `serverErrors.${connectionTest.errorMessage}`,
      {code: connectionTest.responseCode},
    );
  };

  const onSpecificSettingChange = (settingName: string, newValue?: string | number | boolean) => {
    const newSpecificData = {
      ...dataExport.siemSpecificData,
      [settingName]: newValue,
    };
    onFieldChange('siemSpecificData', newSpecificData);
    validateSpecificFields(newSpecificData);
  };

  const getSiemSpecificInputs = () => {
    const selectedStreamType = dataExportTypes[selectedStreamTypeIndex ?? 0];

    if (!selectedStreamType?.specificSettings?.length) {
      return null;
    }

    return selectedStreamType.specificSettings
      .filter((s) => s.name !== 'collectorToken')
      .map((setting) => {
        const label = I18n.t(`dataExportPage.editForm.settings.labels.specific.${setting.name}`);
        if (setting.type === 'boolean') {
          return (
            <div className={classes.settings__switch}>
              <Switch
                label={label}
                isChecked={!!dataExport.siemSpecificData[setting.name]}
                onChange={(e) =>
                  onFieldChange('siemSpecificData', {
                    ...dataExport.siemSpecificData,
                    [setting.name]: e.target.checked,
                  })}
                disabled={!isEditMode}
              />
            </div>
          );
        }
        if (setting.type === 'text' || setting.type === 'number') {
          return (
            <CustomInput
              formControlProps={formControlProps}
              labelText={label}
              id={setting.name}
              inputProps={{
                onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                  onSpecificSettingChange(setting.name, e.target.value),
                type: setting.type,
                value: dataExport.siemSpecificData[setting.name],
                disabled: !isEditMode,
              }}
            />
          );
        }
        if (setting.type === 'dropdown') {
          if (!setting.values) {
            return null;
          }
          const selectedValueIndex = setting.values
            .findIndex((val) => val === dataExport.siemSpecificData[setting.name]);
          return (
            <Dropdown
              customSelectClass="dropdown__select_type"
              disabled={!isEditMode}
              fullWidth={true}
              label={label}
              onChangeValue={(e: React.ChangeEvent<HTMLInputElement>) =>
                onFieldChange('siemSpecificData', {
                  ...dataExport.siemSpecificData,
                  [setting.name]: (setting.values ?? [])[Number(e.target.value)],
                })}
              options={setting.values.map(
                (value) => ({
                  label: value,
                  value,
                }))}
              selectedItemIndex={selectedValueIndex}
            />
          );
        }
        return null;
      });
  };

  const renderConnectionTest = () => (
    <RenderDivOrEmpty isShow={isEditMode} className={classes.settings__connectionTest}>
      <div className={classes.connectionTest__row}>
        <CustomButton
          color="secondary"
          onClick={() => sendTestConnectionToHEC(
            dataExport.collectorUrl,
            dataExport.collectorToken,
            dataExport.streamTypeId,
            dataExport.siemSpecificData,
            (res: DataExportConnectionTestResult) => setConnectionTest(res),
          )(dispatch)}
          disabled={!isURLWithProtocol(dataExport.collectorUrl)
            || !dataExport.streamTypeId}
        >
          {I18n.t('dataExportPage.editForm.settings.buttonTest.name')}
        </CustomButton>

        <span className={classes.connectionTest__resultMessage}>
          {getConnectionTestResultMessage()}
        </span>
      </div>
    </RenderDivOrEmpty>
  );

  return (
    <>
      <div className={classes.settings__templateSection}>
        <Dropdown
          customSelectClass="dropdown__select_template"
          disabled={!isEditMode}
          label={I18n.t('dataExportPage.editForm.settings.labels.copyExport')}
          onChangeValue={handleChangeExportTemplate}
          options={exportTemplateOptions}
          selectedItemIndex={selectedExportTemplateIndex}
        />
      </div>
      <h3>{I18n.t('dataExportPage.editForm.settings.headers.baseSettings')}</h3>
      <div className={classes.inputsRow}>
        <CustomInput
          formControlProps={formControlProps}
          labelText={I18n.t('dataExportPage.editForm.settings.labels.exportName')}
          id="export-name"
          inputProps={{
            onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
              onFieldChange('name', e.target.value),
            type: 'text',
            value: dataExport.name,
            disabled: !isEditMode,
          }}
        />
        <CustomInput
          formControlProps={formControlProps}
          labelText={I18n.t('dataExportPage.editForm.settings.labels.exportDescription')}
          id="export-description"
          inputProps={{
            onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
              onFieldChange('description', e.target.value),
            type: 'text',
            value: dataExport.description,
            disabled: !isEditMode,
          }}
        />
      </div>
      <div className={classes.settings__switch}>
        <Switch
          label={I18n.t('dataExportPage.editForm.settings.labels.enabled')}
          isChecked={dataExport.enabled}
          onChange={(e) => onFieldChange('enabled', e.target.checked)}
          disabled={!isEditMode}
        />
      </div>

      <h3>{I18n.t('dataExportPage.editForm.settings.headers.configureDestination')}</h3>
      <div className={classes.settings__templateSection}>
        <Dropdown
          customSelectClass="dropdown__select_type"
          disabled={!isEditMode}
          fullWidth={true}
          label={I18n.t('dataExportPage.editForm.settings.labels.type')}
          onChangeValue={handleStreamTypeChange}
          options={dataExportTypes.map((type: { name: any; id: any; }) => ({
            label: type.name,
            value: type.id,
          }))}
          selectedItemIndex={selectedStreamTypeIndex}
        />
      </div>

      <CustomInput
        formControlProps={formControlProps}
        labelText={I18n.t('dataExportPage.editForm.settings.labels.eventCollectorURL')}
        id="event-collector-url"
        inputProps={{
          name: 'Event Collector URL',
          onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
            handleCollectorUrlChange(e.target.value),
          type: 'text',
          value: dataExport?.collectorUrl ?? '',
          disabled: !isEditMode
            || dataExportTypes[selectedStreamTypeIndex ?? 0]?.useDefaultCollectorUrlOnly,
          autoComplete: 'off',
        }}
        error={!!collectorUrlError}
        errorText={collectorUrlError}
      />
      <RenderOrEmpty
        isShow={
          !!dataExportTypes[selectedStreamTypeIndex ?? 0]?.specificSettings
            ?.find((s) => s.name === 'collectorToken')
        }
      >
        <CustomInput
          formControlProps={formControlProps}
          labelText={I18n.t('dataExportPage.editForm.settings.labels.specific.collectorToken')}
          id="collector-token"
          inputProps={{
            name: 'collectorToken',
            onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
              onSpecificSettingChange('collectorToken', e.target.value),
            type: showToken ? 'text' : 'password',
            autoComplete: 'new-password',
            value: dataExport?.siemSpecificData?.collectorToken ?? '',
            disabled: !isEditMode,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowToken((prev) => !prev)}
                  onMouseDown={(e: MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                  }}
                  disabled={!isEditMode}
                >
                  {showToken ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </RenderOrEmpty>

      {getSiemSpecificInputs()}

      {renderConnectionTest()}
    </>
  );
}

export default withStyles(style)(DataExportSettingsTab);
